/**
 * Step 3 (Follow Players) of the Build Itinerary wizard.
 *
 * After following teams on Step 2, this is where the coach picks which players to
 * track: expand a followed team and check its players (favoriting == following),
 * or follow the whole team so its games — present and any added later — surface
 * in the itinerary. Scoped to the teams you follow (or have a followed player
 * on), so it stays focused instead of listing every linked team.
 */

import { useState } from "react";
import { useTournamentGames } from "./useTournamentGames";
import { WizardFooter } from "./WizardFooter";
import { TeamAssociationPanel } from "./TeamsStep";
import { tourAnchor } from "../../../../tours/tourAnchors";
import { useTour } from "../../../../tours/useTour";
import { BUILD_ITINERARY_FEATURE, BUILD_ITINERARY_TOURS } from "./buildItineraryTours";

interface FollowPlayersStepProps {
  /** The confirmed tournament. Absent until Step 1 imports + confirms. */
  calendarEventId: string | undefined;
  onNext: () => void;
  onBack?: () => void;
  canGoBack?: boolean;
}

export function FollowPlayersStep({
  calendarEventId,
  onNext,
  onBack,
  canGoBack,
}: FollowPlayersStepProps) {
  // Bumped after a change so the games (and their matched players) refetch.
  const [reloadToken, setReloadToken] = useState(0);
  const gamesState = useTournamentGames(calendarEventId, reloadToken);

  useTour(BUILD_ITINERARY_FEATURE, {
    ready: Boolean(calendarEventId) && gamesState.status === "ready",
    hasData: false,
    pageTourKey: BUILD_ITINERARY_TOURS.followPlayers,
    dataTourKey: null,
  });

  if (!calendarEventId) {
    return (
      <div className="wiz-error-box" data-testid="follow-players-no-tournament">
        <p>
          The imported schedule has not been confirmed into a tournament yet.
          Finish importing on the Event step first.
        </p>
      </div>
    );
  }

  if (gamesState.status === "loading") {
    return (
      <div className="wiz-players-state" data-testid="follow-players-loading">
        <span className="wiz-spinner" aria-hidden="true" />
        <span>Loading your linked teams...</span>
      </div>
    );
  }

  if (gamesState.status === "error") {
    return (
      <div className="wiz-error-box" data-testid="follow-players-error">
        <p role="alert">{gamesState.message}</p>
      </div>
    );
  }

  const games = gamesState.status === "ready" ? gamesState.games : [];

  return (
    <div data-testid="follow-players-step">
      <h2 className="wiz-step-title">Step 3: Follow Players</h2>
      <p className="wiz-sub">
        These are the teams you follow. Expand one to follow individual players, follow the whole
        team, or add more. Their games are highlighted in your itinerary; favorites are followed
        automatically.
      </p>

      <WizardFooter
        onBack={onBack}
        canGoBack={canGoBack}
        legend="Following a player or team surfaces their games in your itinerary. You can change this later."
      >
        <button
          type="button"
          className="wiz-btn wiz-btn--primary"
          onClick={onNext}
          data-testid="follow-players-next"
          {...tourAnchor("build-itinerary.follow.next")}
        >
          Next: Pitchers →
        </button>
      </WizardFooter>

      <TeamAssociationPanel
        calendarEventId={calendarEventId}
        games={games}
        mode="follow"
        tourAnchorId="build-itinerary.follow.list"
        onAssociated={() => setReloadToken((t) => t + 1)}
      />
    </div>
  );
}
